import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  additionalExpenses,
  type AdditionalExpenseItem,
} from '@/api/additionalExpenses';
import { goodsReceipts } from '@/api/goodsReceipts';
import { contractors as contractorsApi, orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import ProjectSelect from '@/components/ProjectSelect';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  contract_id: string;
  expense_type: string;
  supplier_doc_number: string;
  supplier_doc_date: string;
  amount_without_vat: string;
  vat_rate_id: string;
  vat_amount: string;
  comment: string;
  project_id: string | null;
  items: AdditionalExpenseItem[];
  source_receipt_id: string;
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  contractor_id: '',
  contract_id: '',
  expense_type: 'TRANSPORT',
  supplier_doc_number: '',
  supplier_doc_date: '',
  amount_without_vat: '0',
  vat_rate_id: '',
  vat_amount: '0',
  comment: '',
  project_id: null,
  items: [],
  source_receipt_id: '',
};

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AdditionalExpenseEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по purchases.receipt.create permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('purchases.receipt.create');
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });
  const nomList = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.list({ limit: 500 }),
  });
  const vatList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  const docQuery = useQuery({
    queryKey: ['additional-expense', id],
    queryFn: () => additionalExpenses.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const numberPreview = useNumberPreview('doc.additional_expenses', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  // Договоры выбранного контрагента
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });

  // Поступления для выбора
  const receiptsQ = useQuery({
    queryKey: ['goods-receipts-for-ae', form.organization_id],
    queryFn: () => goodsReceipts.list({
      organization_id: form.organization_id, posted: true, limit: 200,
    }),
    enabled: !!form.organization_id,
  });
  const sourceReceiptQ = useQuery({
    queryKey: ['goods-receipt-full', form.source_receipt_id],
    queryFn: () => goodsReceipts.get(form.source_receipt_id),
    enabled: !!form.source_receipt_id,
  });

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        contractor_id: d.contractor_id,
        contract_id: d.contract_id,
        expense_type: d.expense_type,
        supplier_doc_number: d.supplier_doc_number ?? '',
        supplier_doc_date: d.supplier_doc_date ?? '',
        amount_without_vat: String(d.amount_without_vat),
        vat_rate_id: d.vat_rate_id ?? '',
        vat_amount: String(d.vat_amount),
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        items: d.items.map((it) => ({
          line_number: it.line_number,
          goods_receipt_item_id: it.goods_receipt_item_id,
          distribution_base: String(it.distribution_base),
          allocated_amount: it.allocated_amount,
        })),
        source_receipt_id: '',
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const nomName = useMemo(() => {
    const m = new Map<string, string>();
    nomList.data?.items.forEach((n) => m.set(n.id, n.name));
    return m;
  }, [nomList.data]);

  const [riCache, setRiCache] = useState<Record<string, { nom: string; qty: string; amount: string }>>({});
  useEffect(() => {
    if (sourceReceiptQ.data) {
      setRiCache((c) => {
        const next = { ...c };
        for (const it of sourceReceiptQ.data!.items) {
          if (it.id) {
            next[it.id] = {
              nom: it.nomenclature_id,
              qty: it.quantity,
              amount: it.amount_without_vat,
            };
          }
        }
        return next;
      });
    }
  }, [sourceReceiptQ.data]);

  const totalBase = form.items.reduce((s, e) => s + Number(e.distribution_base || 0), 0);
  const totalAmount = Number(form.amount_without_vat || 0) + Number(form.vat_amount || 0);

  // Превью розподілу (для UX перед проведенням)
  const allocatedPreview = useMemo(() => {
    const amt = Number(form.amount_without_vat || 0);
    if (amt <= 0 || totalBase <= 0) return form.items.map(() => 0);
    const result: number[] = [];
    let acc = 0;
    for (let i = 0; i < form.items.length - 1; i++) {
      const it = form.items[i]!;
      const share = Math.round((amt * Number(it.distribution_base || 0) / totalBase) * 10000) / 10000;
      result.push(share);
      acc += share;
    }
    result.push(Math.round((amt - acc) * 10000) / 10000);
    return result;
  }, [form.items, form.amount_without_vat, totalBase]);

  const create = useMutation({
    mutationFn: () => additionalExpenses.create(buildPayload(form)),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['additional-expenses'] });
      navigate(`/additional-expenses/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => additionalExpenses.update(id!, buildPayload(form)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['additional-expense', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: зберегти та провести одним кліком (create → post).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await additionalExpenses.create(buildPayload(form));
      await additionalExpenses.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['additional-expenses'] });
      navigate(`/additional-expenses/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: спершу зберегти правки, потім провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await additionalExpenses.update(id!, buildPayload(form));
      await additionalExpenses.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['additional-expense', id] });
      qc.invalidateQueries({ queryKey: ['additional-expenses'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => additionalExpenses.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['additional-expense', id] });
      qc.invalidateQueries({ queryKey: ['additional-expenses'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => additionalExpenses.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['additional-expenses'] });
      navigate('/additional-expenses');
    },
  });

  function setItem(i: number, patch: Partial<AdditionalExpenseItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }
  function addReceiptItem(itemId: string) {
    if (!sourceReceiptQ.data) return;
    const ri = sourceReceiptQ.data.items.find((x) => x.id === itemId);
    if (!ri) return;
    if (form.items.some((x) => x.goods_receipt_item_id === itemId)) return;
    setForm((f) => ({
      ...f,
      items: [...f.items, {
        line_number: f.items.length + 1,
        goods_receipt_item_id: itemId,
        distribution_base: ri.amount_without_vat,
      }],
    }));
  }

  function onVatRateChange(rid: string) {
    const r = vatList.data?.find((v) => v.id === rid);
    const rate = r?.rate_percent ?? 0;
    const amt = Number(form.amount_without_vat || 0);
    setForm({
      ...form,
      vat_rate_id: rid,
      vat_amount: ((amt * rate) / 100).toFixed(2),
    });
  }
  function onAmountChange(v: string) {
    const r = vatList.data?.find((x) => x.id === form.vat_rate_id);
    const rate = r?.rate_percent ?? 0;
    setForm({
      ...form,
      amount_without_vat: v,
      vat_amount: ((Number(v || 0) * rate) / 100).toFixed(2),
    });
  }

  // UX β: warning якщо новий документ а user не має purchases.receipt.create у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати додаткові витрати у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/additional-expenses')}>Скасувати</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нові додаткові витрати' : `Дод. витрати: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/additional-expenses')}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => isNew ? createAndPost.mutate() : updateAndPost.mutate()}
          postDisabled={
            isNew
              ? create.isPending || createAndPost.isPending
              : updateAndPost.isPending || update.isPending || form.items.length === 0
          }
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }) }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/additional-expenses/${id}/print`)}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>№ документа</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Вид витрати</label>
          <select value={form.expense_type}
                  onChange={(e) => setForm({ ...form, expense_type: e.target.value })}
                  disabled={isPosted}>
            <option value="TRANSPORT">Транспорт</option>
            <option value="INSURANCE">Страхування</option>
            <option value="CUSTOMS_BROKER">Митний брокер</option>
            <option value="OTHER">Інше</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Постачальник послуги</label>
          <select value={form.contractor_id}
                  onChange={(e) => setForm({ ...form, contractor_id: e.target.value, contract_id: '' })}
                  disabled={isPosted}>
            <option value="">—</option>
            {contractorsQ.data?.items.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Договір</label>
          <select value={form.contract_id}
                  onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                  disabled={isPosted || !form.contractor_id}>
            <option value="">—</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>№ акту</label>
          <input value={form.supplier_doc_number}
                 onChange={(e) => setForm({ ...form, supplier_doc_number: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Дата акту</label>
          <DatePopover value={form.supplier_doc_date}
                 onChange={(v) => setForm({ ...form, supplier_doc_date: v })}
                 disabled={isPosted} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Сума послуги без ПДВ</label>
          <input type="number" step="0.01" value={form.amount_without_vat}
                 onChange={(e) => onAmountChange(e.target.value)}
                 disabled={isPosted}
                 style={{ textAlign: 'right' }} />
        </div>
        <div className="field">
          <label>Ставка ПДВ</label>
          <select value={form.vat_rate_id}
                  onChange={(e) => onVatRateChange(e.target.value)}
                  disabled={isPosted}>
            <option value="">— (без ПДВ)</option>
            {vatList.data?.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Сума ПДВ</label>
          <input type="number" step="0.01" value={form.vat_amount}
                 onChange={(e) => setForm({ ...form, vat_amount: e.target.value })}
                 disabled={isPosted}
                 style={{ textAlign: 'right' }} />
        </div>
        <div className="field">
          <label>Усього</label>
          <input value={fmt(totalAmount)} readOnly
                 style={{ textAlign: 'right', background: '#f8fafc' }} />
        </div>
      </div>

      {!isPosted && (
        <div style={{
          marginTop: '1rem', padding: '0.75rem', background: '#f8fafc', borderRadius: 6,
          display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.75rem',
        }}>
          <div className="field">
            <label>Додати з поступлення</label>
            <select value={form.source_receipt_id}
                    onChange={(e) => setForm({ ...form, source_receipt_id: e.target.value })}>
              <option value="">— оберіть проведене поступлення —</option>
              {receiptsQ.data?.items.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.document_number} від {new Date(r.document_date).toLocaleDateString('uk-UA')}
                  {' — '}{fmt(Number(r.total_amount))} грн
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Рядки поступлення</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
              {sourceReceiptQ.data?.items.map((ri) => {
                const used = form.items.some((x) => x.goods_receipt_item_id === ri.id);
                return (
                  <button key={ri.id} type="button"
                          onClick={() => ri.id && addReceiptItem(ri.id)}
                          disabled={used}
                          style={{
                            padding: '0.3rem 0.6rem', fontSize: '0.85rem',
                            opacity: used ? 0.4 : 1,
                          }}>
                    {used ? '✓ ' : '+ '}
                    {nomName.get(ri.nomenclature_id) ?? 'товар'} ({ri.quantity}, {fmt(Number(ri.amount_without_vat))} грн)
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <h3 style={{ marginTop: '1.5rem' }}>
        Розподіл по рядках поступлення (за сумою)
      </h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 30 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Товар (з поступлення)</th>
            <th style={{ padding: '0.5rem', width: 130 }}>База розподілу</th>
            <th style={{ padding: '0.5rem', width: 130 }}>Буде розподілено</th>
            <th style={{ padding: '0.5rem', width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => {
            const ri = riCache[it.goods_receipt_item_id];
            const label = ri
              ? `${nomName.get(ri.nom) ?? 'товар'} × ${ri.qty}`
              : it.goods_receipt_item_id.slice(0, 8) + '…';
            const alloc = isPosted
              ? Number(it.allocated_amount ?? 0)
              : (allocatedPreview[i] ?? 0);
            return (
              <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
                <td style={{ padding: '0.5rem', color: '#475569' }}>{label}</td>
                <td style={{ padding: '0.5rem' }}>
                  <input type="number" step="0.01" value={it.distribution_base}
                         onChange={(e) => setItem(i, { distribution_base: e.target.value })}
                         disabled={isPosted}
                         style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>
                  {fmt(alloc)}
                </td>
                <td style={{ padding: '0.5rem' }}>
                  {!isPosted && (
                    <button onClick={() => removeRow(i)} className="danger"
                            style={{ padding: '0.3rem 0.6rem' }}>×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
            <td colSpan={2} style={{ padding: '0.5rem', textAlign: 'right' }}>Усього:</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalBase)}</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(Number(form.amount_without_vat || 0))}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      <div className="field">
        <label>Проект / напрям</label>
        <ProjectSelect
          value={form.project_id}
          organizationId={form.organization_id}
          onChange={(v) => setForm({ ...form, project_id: v })}
          disabled={isPosted} />
      </div>

      <div className="field" style={{ marginTop: '1rem' }}>
        <label>Коментар</label>
        <input value={form.comment}
               onChange={(e) => setForm({ ...form, comment: e.target.value })}
               disabled={isPosted} />
      </div>

      {error && <div className="error">{error}</div>}

      {!isPosted && (
        <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '0.85rem' }}>
          <strong>Як працює:</strong> сума послуги без ПДВ розподіляється
          пропорційно «базі розподілу» по рядках поступлення (за замовчуванням
          база = сума рядка). Для кожного рядка створюється проводка
          <strong> Дт 281 — Кт 631</strong> на розподілену частку та
          оновлюється собівартість партії, створеної відповідним поступленням.
          ПДВ <strong>Дт 6442 — Кт 631</strong> — одною проводкою на весь
          документ (податковий кредит). Розпровести можна лише якщо з партій
          ще нічого не списано.
        </p>
      )}
    </div>
  );
}

function buildPayload(form: FormState) {
  return {
    organization_id: form.organization_id,
    document_number: form.document_number,
    document_date: new Date(form.document_date).toISOString(),
    contractor_id: form.contractor_id,
    contract_id: form.contract_id,
    expense_type: form.expense_type,
    distribute_by: 'AMOUNT',
    supplier_doc_number: form.supplier_doc_number || null,
    supplier_doc_date: form.supplier_doc_date || null,
    amount_without_vat: form.amount_without_vat || '0',
    vat_rate_id: form.vat_rate_id || null,
    vat_amount: form.vat_amount || '0',
    comment: form.comment || null,
    items: form.items.map((it) => ({
      line_number: it.line_number,
      goods_receipt_item_id: it.goods_receipt_item_id,
      distribution_base: it.distribution_base,
    })),
  };
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
